/**
 * Convert org-mode text into Markdown for print-like output (the reMarkable
 * export, see lib/remarkable_mcp.js).
 *
 * The target is the renderer of remarkable-mcp's `remarkable_markdown_to_pdf`:
 * markdown-it in its "commonmark" preset plus tables, with raw HTML escaped.
 * So the output sticks to CommonMark and pipe tables: no HTML, no
 * strikethrough, no task lists. Things that only make sense inside Emacs
 * (drawers, comments, #+KEYWORD lines, export-only blocks) are dropped.
 *
 * It works on raw org text rather than on organice's parsed headers because
 * the parser leaves blocks (#+BEGIN_SRC …) and drawers other than
 * PROPERTIES/LOGBOOK inside the raw description, and both a whole file and
 * one exported subtree are plain org text anyway.
 */

const DEFAULT_TODO_KEYWORDS = ['TODO', 'DONE'];

const PLANNING_LINE = /^\s*(?:(?:SCHEDULED|DEADLINE|CLOSED):\s*[<[][^>\]]*[>\]]\s*)+$/;
const PLANNING_ITEM = /(SCHEDULED|DEADLINE|CLOSED):\s*([<[][^>\]]*[>\]])/g;
const DRAWER_START = /^\s*:([A-Za-z0-9_-]+):\s*$/;
const DRAWER_END = /^\s*:END:\s*$/i;
const BLOCK_START = /^\s*#\+BEGIN_(\w+)(?:\s+(.*))?$/i;
const KEYWORD_LINE = /^\s*#\+(\w+):\s*(.*)$/;
const COMMENT_LINE = /^\s*#(?:\s.*)?$/;
const FIXED_WIDTH = /^(\s*):(?: (.*))?$/;
const HORIZONTAL_RULE = /^\s*-{5,}\s*$/;
const TABLE_LINE = /^\s*\|/;
const TABLE_SEPARATOR = /^\s*\|[-+:| ]*-[-+:| ]*\|?\s*$/;
const HEADING = /^(\*+)\s+(.*?)\s*$/;
// `* item` at column 0 is a heading, which is matched before list items.
const LIST_ITEM = /^(\s*)([-+*]|\d+[.)])(?:\s+(.*))?$/;

const PLANNING_LABELS = { SCHEDULED: 'Scheduled', DEADLINE: 'Deadline', CLOSED: 'Closed' };

const PRETTY_LINK_SCHEMES = /^(https?|mailto|ftp):/i;

// Characters that mean something to CommonMark inline parsing.
const escapeInline = (text) => text.replace(/([\\`*_[\]<>!|])/g, '\\$1');

// Characters that would start a block construct at the beginning of a line.
const escapeLineStart = (text) =>
  text
    .replace(/^(\s*)([#>+=-])/, '$1\\$2')
    .replace(/^(\s*)(\d+)([.)])/, '$1$2\\$3')
    .replace(/^(\s*)~~~/, '$1\\~~~');

const timestampText = (raw) => raw.slice(1, -1).trim();

const linkDestination = (url) => (/[\s()<>]/.test(url) ? `<${url.replace(/[<>]/g, '')}>` : url);

// Org emphasis: the marker must sit after start/whitespace/opening punctuation
// and before end/whitespace/closing punctuation, around non-blank contents.
const EMPHASIS_PRE = /[\s\-({'"]/;
const EMPHASIS_POST = /[\s\-.,:!?;'")}[]/;

const MARKERS = {
  '*': (inner) => `**${renderInline(inner)}**`,
  '/': (inner) => `*${renderInline(inner)}*`,
  _: (inner) => `*${renderInline(inner)}*`,
  // CommonMark has no strikethrough; the tildes still read as "struck".
  '+': (inner) => `~~${renderInline(inner)}~~`,
  '=': (inner) => codeSpan(inner),
  '~': (inner) => codeSpan(inner),
};

const codeSpan = (text) => {
  const longestRun = Math.max(0, ...(text.match(/`+/g) || []).map((run) => run.length));
  const fence = '`'.repeat(longestRun + 1);
  const pad = text.startsWith('`') || text.endsWith('`') ? ' ' : '';
  return `${fence}${pad}${text}${pad}${fence}`;
};

const findEmphasis = (text, from) => {
  for (let i = from; i < text.length; i++) {
    const marker = text[i];
    if (!MARKERS[marker]) continue;
    if (i > 0 && !EMPHASIS_PRE.test(text[i - 1])) continue;
    if (i + 1 >= text.length || /\s/.test(text[i + 1]) || text[i + 1] === marker) continue;
    for (let j = i + 2; j < text.length; j++) {
      if (text[j] !== marker) continue;
      if (/\s/.test(text[j - 1])) continue;
      if (j + 1 < text.length && !EMPHASIS_POST.test(text[j + 1])) continue;
      return { index: i, length: j - i + 1, render: () => MARKERS[marker](text.slice(i + 1, j)) };
    }
  }
  return null;
};

const INLINE_PATTERNS = [
  {
    regex: /\[\[([^\]]+)\](?:\[([^\]]+)\])?\]/g,
    render: ([, url, description]) => {
      if (PRETTY_LINK_SCHEMES.test(url)) {
        return description
          ? `[${renderInline(description)}](${linkDestination(url)})`
          : `<${url.replace(/[<>]/g, '')}>`;
      }
      // id:, file:, roam: and *heading links point nowhere on paper.
      return renderInline(description || url.replace(/^(id|file|roam|attachment):/, ''));
    },
  },
  {
    regex: /<\d{4}-\d{2}-\d{2}[^>\n]*>|\[\d{4}-\d{2}-\d{2}[^\]\n]*\]/g,
    render: ([raw]) => escapeInline(timestampText(raw)),
  },
  {
    regex: /\b(?:https?|ftp):\/\/[^\s<>\]]*[^\s<>\].,;:!?)'"]/g,
    render: ([url]) => `<${url}>`,
  },
];

const earliestPatternMatch = (text) => {
  let best = null;
  INLINE_PATTERNS.forEach((pattern) => {
    pattern.regex.lastIndex = 0;
    const match = pattern.regex.exec(text);
    if (match && (!best || match.index < best.index)) {
      best = { index: match.index, length: match[0].length, render: () => pattern.render(match) };
    }
  });
  return best;
};

/**
 * Render one line (or a fragment of one) of org inline markup as Markdown.
 */
export const renderInline = (text) => {
  let output = '';
  let rest = text;
  while (rest.length) {
    const pattern = earliestPatternMatch(rest);
    const emphasis = findEmphasis(rest, 0);
    let next = pattern;
    // Emphasis that starts before a link wraps it (`*see [[url][here]]*`),
    // unless the link starts first.
    if (emphasis && (!pattern || emphasis.index < pattern.index)) {
      const end = emphasis.index + emphasis.length;
      // An emphasis span must not cut through a link or timestamp.
      if (!pattern || pattern.index >= end || pattern.index + pattern.length <= end) {
        next = emphasis;
      }
    }
    if (!next) {
      output += escapeInline(rest);
      break;
    }
    output += escapeInline(rest.slice(0, next.index)) + next.render();
    rest = rest.slice(next.index + next.length);
  }
  return output;
};

const todoKeywordsFromText = (lines) => {
  const keywords = [];
  lines.forEach((line) => {
    const match = line.match(/^\s*#\+(?:TODO|SEQ_TODO|TYP_TODO):\s*(.*)$/i);
    if (!match) return;
    match[1]
      .split(/\s+/)
      .filter((word) => word && word !== '|')
      .forEach((word) => keywords.push(word.replace(/\(.*\)$/, '')));
  });
  return keywords;
};

const parseHeading = (rest, todoKeywords) => {
  let text = rest;
  let keyword = null;
  const firstWord = text.split(/\s+/)[0];
  if (todoKeywords.includes(firstWord)) {
    keyword = firstWord;
    text = text.slice(firstWord.length).trimStart();
  }
  let tags = [];
  const tagMatch = text.match(/^(.*?)\s+(:[^\s]+:)$/) || text.match(/^()(:[^\s]+:)$/);
  if (tagMatch) {
    text = tagMatch[1];
    tags = tagMatch[2].split(':').filter(Boolean);
  }
  return { keyword, title: text.trim(), tags };
};

const renderTable = (rows) => {
  const hasSeparator = rows.some((row) => TABLE_SEPARATOR.test(row));
  const cellsOf = (row) => {
    const trimmed = row.trim().replace(/^\|/, '').replace(/\|$/, '');
    return trimmed.split('|').map((cell) => renderInline(cell.trim()));
  };
  const groups = [[]];
  rows.forEach((row) => {
    if (TABLE_SEPARATOR.test(row)) {
      if (groups[groups.length - 1].length) groups.push([]);
    } else {
      groups[groups.length - 1].push(cellsOf(row));
    }
  });
  const nonEmptyGroups = groups.filter((group) => group.length);
  if (!nonEmptyGroups.length) return [];
  // Org tables need no header; Markdown tables do. A table without a rule
  // gets its first row promoted, a table with one keeps its own header.
  let header;
  let body;
  if (hasSeparator && nonEmptyGroups.length > 1) {
    header = nonEmptyGroups[0][nonEmptyGroups[0].length - 1];
    body = [].concat(...nonEmptyGroups.slice(1));
    if (nonEmptyGroups[0].length > 1) body = nonEmptyGroups[0].slice(0, -1).concat(body);
  } else {
    const all = [].concat(...nonEmptyGroups);
    header = all[0];
    body = all.slice(1);
  }
  const width = Math.max(header.length, ...body.map((row) => row.length));
  const pad = (row) => row.concat(Array(width - row.length).fill(''));
  const line = (row) => `| ${pad(row).join(' | ')} |`;
  return [line(header), `|${' --- |'.repeat(width)}`, ...body.map(line)];
};

/**
 * @param {string} orgText org-mode source (a whole file or one subtree)
 * @param {object} [options]
 * @param {string} [options.title] document title, emitted as `# title`; falls
 *   back to the text's own `#+TITLE:` line
 * @param {string[]} [options.todoKeywords] extra TODO keywords (the file's
 *   `#+TODO:` lines are read from the text itself)
 * @param {boolean} [options.includePlanning=true] keep SCHEDULED/DEADLINE/CLOSED
 * @param {boolean} [options.includeTags=true] keep headline tags
 * @returns {string} Markdown
 */
export const orgToMarkdown = (orgText, options = {}) => {
  const { includePlanning = true, includeTags = true } = options;
  const lines = (orgText || '').replace(/\r\n?/g, '\n').split('\n');
  const todoKeywords = DEFAULT_TODO_KEYWORDS.concat(
    options.todoKeywords || [],
    todoKeywordsFromText(lines)
  );

  let title = options.title || null;
  if (!title) {
    const titleLine = lines.find((line) => /^\s*#\+TITLE:/i.test(line));
    if (titleLine) title = titleLine.replace(/^\s*#\+TITLE:\s*/i, '').trim() || null;
  }

  const headingLevels = lines.map((line) => line.match(HEADING)).filter(Boolean);
  const minLevel = headingLevels.length
    ? Math.min(...headingLevels.map((match) => match[1].length))
    : 1;
  const baseLevel = title ? 2 : 1;

  const out = [];
  const blank = () => {
    if (out.length && out[out.length - 1] !== '') out.push('');
  };
  if (title) {
    out.push(`# ${renderInline(title)}`);
    out.push('');
  }

  // Open list items, innermost last: the org indentation of the item's
  // marker, and the Markdown indentation its contents need.
  let listStack = [];
  // `continuesList` is false for anything but a new list item: text that
  // leaves a list (or a sublist) needs a blank line first, or CommonMark's
  // lazy continuation would glue it onto the last item.
  const listIndentFor = (indent, continuesList = false) => {
    let popped = false;
    while (listStack.length && indent <= listStack[listStack.length - 1].orgIndent) {
      listStack.pop();
      popped = true;
    }
    if (popped && !continuesList) blank();
    return listStack.length ? listStack[listStack.length - 1].contentIndent : '';
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const indent = line.match(/^\s*/)[0].length;

    if (!line.trim()) {
      blank();
      i++;
      continue;
    }

    const heading = line.match(HEADING);
    if (heading) {
      listStack = [];
      const { keyword, title: headingTitle, tags } = parseHeading(heading[2], todoKeywords);
      const level = heading[1].length - minLevel + baseLevel;
      let text = renderInline(headingTitle);
      if (keyword) text = text ? `${keyword} ${text}` : keyword;
      if (includeTags && tags.length) text += ` *(${tags.map(escapeInline).join(', ')})*`;
      blank();
      out.push(level <= 6 ? `${'#'.repeat(level)} ${text}` : `**${text}**`);
      out.push('');
      i++;
      continue;
    }

    if (PLANNING_LINE.test(line)) {
      if (includePlanning) {
        const items = [];
        line.replace(PLANNING_ITEM, (_, kind, stamp) => {
          items.push(`${PLANNING_LABELS[kind]}: ${escapeInline(timestampText(stamp))}`);
          return '';
        });
        blank();
        out.push(`*${items.join(' · ')}*`);
        out.push('');
      }
      i++;
      continue;
    }

    const drawer = line.match(DRAWER_START);
    if (drawer && drawer[1].toUpperCase() !== 'END') {
      const end = lines.findIndex((candidate, index) => index > i && DRAWER_END.test(candidate));
      if (end !== -1) {
        i = end + 1;
        continue;
      }
    }

    const block = line.match(BLOCK_START);
    if (block) {
      const kind = block[1].toUpperCase();
      const endPattern = new RegExp(`^\\s*#\\+END_${block[1]}\\s*$`, 'i');
      let end = lines.findIndex((candidate, index) => index > i && endPattern.test(candidate));
      if (end === -1) end = lines.length;
      const prefix = listIndentFor(indent);
      const body = lines.slice(i + 1, end);
      const bodyIndent = Math.min(
        ...body.filter((l) => l.trim()).map((l) => l.match(/^\s*/)[0].length),
        Infinity
      );
      const dedented = body.map((l) => (bodyIndent === Infinity ? l : l.slice(bodyIndent)));
      const exportBackend = (block[2] || '').trim().split(/\s+/)[0].toLowerCase();

      if (kind === 'SRC' || kind === 'EXAMPLE') {
        const language = kind === 'SRC' ? exportBackend : '';
        const longestRun = Math.max(
          2,
          ...dedented.map((l) => (l.match(/^\s*(`+)/) || ['', ''])[1].length)
        );
        const fence = '`'.repeat(longestRun + 1);
        blank();
        out.push(`${prefix}${fence}${language}`);
        dedented.forEach((l) => out.push(l ? `${prefix}${l}` : ''));
        out.push(`${prefix}${fence}`);
        out.push('');
      } else if (kind === 'QUOTE' || kind === 'VERSE') {
        blank();
        dedented.forEach((l, index) => {
          if (!l.trim()) {
            out.push(`${prefix}>`);
          } else {
            const hardBreak = kind === 'VERSE' && index < dedented.length - 1 ? '\\' : '';
            out.push(`${prefix}> ${escapeLineStart(renderInline(l.trim()))}${hardBreak}`);
          }
        });
        out.push('');
      } else if (kind === 'EXPORT') {
        if (['markdown', 'md'].includes(exportBackend)) {
          blank();
          dedented.forEach((l) => out.push(l));
          out.push('');
        }
      } else if (kind !== 'COMMENT') {
        // CENTER and custom blocks: keep the contents as paragraphs.
        blank();
        dedented.forEach((l) =>
          out.push(l.trim() ? `${prefix}${escapeLineStart(renderInline(l.trim()))}` : '')
        );
        out.push('');
      }
      i = end + 1;
      continue;
    }

    if (KEYWORD_LINE.test(line) || COMMENT_LINE.test(line)) {
      i++;
      continue;
    }

    if (HORIZONTAL_RULE.test(line)) {
      listStack = [];
      blank();
      out.push('---');
      out.push('');
      i++;
      continue;
    }

    if (TABLE_LINE.test(line)) {
      const prefix = listIndentFor(indent);
      const rows = [];
      while (i < lines.length && TABLE_LINE.test(lines[i])) rows.push(lines[i++]);
      blank();
      renderTable(rows).forEach((row) => out.push(`${prefix}${row}`));
      out.push('');
      continue;
    }

    if (FIXED_WIDTH.test(line)) {
      const prefix = listIndentFor(indent);
      blank();
      out.push(`${prefix}\`\`\``);
      while (i < lines.length && FIXED_WIDTH.test(lines[i])) {
        out.push(`${prefix}${lines[i].match(FIXED_WIDTH)[2] || ''}`);
        i++;
      }
      out.push(`${prefix}\`\`\``);
      out.push('');
      continue;
    }

    const item = line.match(LIST_ITEM);
    if (item) {
      const wasInList = listStack.length > 0;
      const prefix = listIndentFor(indent, true);
      const ordered = /\d/.test(item[2]);
      const marker = ordered ? `${parseInt(item[2], 10)}.` : '-';
      let content = item[3] || '';
      let lead = '';
      const forced = content.match(/^\[@(\d+)\]\s*/);
      let number = marker;
      if (ordered && forced) {
        number = `${forced[1]}.`;
        content = content.slice(forced[0].length);
      }
      const checkbox = content.match(/^\[([ xX-])\]\s*/);
      if (checkbox) {
        lead = `\\[${checkbox[1] === ' ' ? ' ' : checkbox[1].toLowerCase()}\\] `;
        content = content.slice(checkbox[0].length);
      }
      const description = !ordered && content.match(/^(.*?)\s+::(?:\s+(.*))?$/);
      let rendered;
      if (description) {
        rendered = `**${renderInline(description[1])}**${
          description[2] ? ` — ${renderInline(description[2])}` : ''
        }`;
      } else {
        rendered = escapeLineStart(renderInline(content));
      }
      // An ordered list not starting at 1 cannot interrupt a paragraph.
      if (!wasInList) blank();
      const bullet = ordered ? number : marker;
      out.push(`${prefix}${bullet} ${lead}${rendered}`.replace(/\s+$/, ''));
      listStack.push({ orgIndent: indent, contentIndent: prefix + ' '.repeat(bullet.length + 1) });
      i++;
      continue;
    }

    // Paragraph text, possibly the continuation of a list item.
    const prefix = listIndentFor(indent);
    let text = line.trim();
    let hardBreak = '';
    if (text.endsWith('\\\\')) {
      text = text.slice(0, -2).trimEnd();
      hardBreak = '\\';
    }
    out.push(`${prefix}${escapeLineStart(renderInline(text))}${hardBreak}`);
    i++;
  }

  while (out.length && out[out.length - 1] === '') out.pop();
  return out.join('\n') + '\n';
};
